import { useState } from 'react';
import { Copy, ExternalLink, CheckCircle2, Share2, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Detects Instagram's in-app browser and shows a prompt to open in Safari/Chrome
 * Instagram's WebView has limitations with localStorage, camera API, and cookies
 */

// Detect Instagram browser synchronously (runs once on module load)
const detectInAppBrowser = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { isInApp: false, browser: null, isAndroid: false, isIOS: false };
  }
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
  const ua = userAgent.toLowerCase();
  
  // Platform detection
  const isAndroid = /android/i.test(userAgent);
  const isIOS = /iphone|ipad|ipod/i.test(userAgent);
  
  // Instagram browser identifiers (multiple patterns for robustness)
  const isInstagram = ua.includes('instagram') || ua.includes('ig ') || /instagram/i.test(userAgent);
  
  // Facebook in-app browser
  const isFacebookBrowser = ua.includes('fban') || ua.includes('fbav') || ua.includes('fb_iab') || ua.includes('facebook');
  
  // Other social media in-app browsers
  const isLinkedInBrowser = ua.includes('linkedin');
  const isTwitterBrowser = ua.includes('twitter');
  const isSnapchatBrowser = ua.includes('snapchat');
  const isTikTokBrowser = ua.includes('tiktok') || ua.includes('bytedance');
  
  // Generic WebView detection for Android
  const isAndroidWebView = isAndroid && (ua.includes('wv') || ua.includes('webview'));
  
  let browser = null;
  if (isInstagram) browser = 'Instagram';
  else if (isFacebookBrowser) browser = 'Facebook';
  else if (isLinkedInBrowser) browser = 'LinkedIn';
  else if (isTwitterBrowser) browser = 'Twitter';
  else if (isSnapchatBrowser) browser = 'Snapchat';
  else if (isTikTokBrowser) browser = 'TikTok';
  else if (isAndroidWebView) browser = 'WebView';
  
  const isInApp = isInstagram || isFacebookBrowser || isLinkedInBrowser || isTwitterBrowser || isSnapchatBrowser || isTikTokBrowser || isAndroidWebView;
  
  // Log for debugging (only in development)
  if (process.env.NODE_ENV === 'development') {
    console.log('Browser Detection:', { userAgent, isInApp, browser, isAndroid, isIOS });
  }
  
  return { isInApp, browser, isAndroid, isIOS };
};

// Check once on module load
const BROWSER_INFO = detectInAppBrowser();

const InstagramBrowserDetector = ({ children }) => {
  const [copied, setCopied] = useState(false);
  
  // Check if Web Share API is available (computed once)
  const showShareOption = typeof navigator !== 'undefined' && !!navigator.share;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (e) {
        console.error('Copy failed:', e);
      }
      document.body.removeChild(textArea);
    }
  };

  const openInBrowser = () => {
    const url = window.location.href;
    
    if (BROWSER_INFO.isAndroid) {
      // Android: Use intent:// URL scheme to open in Chrome or default browser
      // This is the most reliable way to escape Instagram's WebView on Android
      const urlWithoutProtocol = url.replace(/^https?:\/\//, '');
      
      // Try Chrome first, then fall back to default browser
      const intentUrl = `intent://${urlWithoutProtocol}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url)};end`;
      
      window.location.href = intentUrl;
      
      // Fallback after delay if intent doesn't work
      setTimeout(() => {
        // Try generic browser intent
        const genericIntent = `intent://${urlWithoutProtocol}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
        window.location.href = genericIntent;
      }, 1000);
    } else if (BROWSER_INFO.isIOS) {
      // iOS: Try Safari URL scheme
      // x-safari-https:// works on some iOS versions
      window.location.href = `x-safari-${url}`;
      
      // Fallback after small delay
      setTimeout(() => {
        window.open(url, '_blank');
      }, 500);
    } else {
      // Desktop or other - standard approach
      window.open(url, '_blank');
    }
  };

  const shareLink = async () => {
    try {
      await navigator.share({
        title: 'Ophilia',
        text: 'Join me on Ophilia!',
        url: window.location.href,
      });
    } catch (err) {
      // User cancelled or share failed - fall back to copy
      copyLink();
    }
  };

  if (BROWSER_INFO.isInApp) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
              <span className="text-white text-xl">◯</span>
            </div>
            <span className="text-2xl text-stone-800 font-medium">Ophilia</span>
          </div>

          {/* Warning Icon */}
          <div className="w-20 h-20 mx-auto mb-6 bg-orange-100 rounded-full flex items-center justify-center">
            <ExternalLink className="w-10 h-10 text-orange-600" />
          </div>

          {/* Message */}
          <h2 className="text-xl text-stone-800 font-medium mb-3">
            Open in Your Browser
          </h2>
          <p className="text-stone-500 mb-2 text-sm leading-relaxed">
            {BROWSER_INFO.browser ? `${BROWSER_INFO.browser}'s` : "This app's"} browser has limitations that prevent the full Ophilia experience.
          </p>
          <p className="text-stone-500 mb-6 text-sm leading-relaxed">
            Please open this link in <strong>{BROWSER_INFO.isIOS ? 'Safari' : 'Chrome'}</strong> to continue.
          </p>

          {/* Platform-specific Instructions */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="w-4 h-4 text-orange-500" />
              <p className="text-sm font-medium text-stone-600">
                {BROWSER_INFO.isAndroid ? 'Android' : BROWSER_INFO.isIOS ? 'iPhone' : 'Mobile'} Instructions:
              </p>
            </div>
            
            {BROWSER_INFO.isAndroid ? (
              <ol className="text-sm text-stone-500 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="bg-orange-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <span>Tap the <strong>⋮</strong> menu (three dots, top right)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-orange-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <span>Select <strong>&ldquo;Open in Chrome&rdquo;</strong> or <strong>&ldquo;Open in Browser&rdquo;</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-orange-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">3</span>
                  <span>If that doesn&apos;t work, use <strong>&ldquo;Copy Link&rdquo;</strong> below and paste in Chrome</span>
                </li>
              </ol>
            ) : (
              <ol className="text-sm text-stone-500 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="bg-orange-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">1</span>
                  <span>Tap the <strong>⋯</strong> menu (top right)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="bg-orange-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs flex-shrink-0">2</span>
                  <span>Select <strong>&ldquo;Open in Safari&rdquo;</strong> or <strong>&ldquo;Open in Browser&rdquo;</strong></span>
                </li>
              </ol>
            )}
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button
              onClick={openInBrowser}
              className="w-full h-12 bg-orange-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-full"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Open in {BROWSER_INFO.isIOS ? 'Safari' : 'Chrome'}
            </Button>
            
            <Button
              onClick={copyLink}
              variant="outline"
              className="w-full h-12 rounded-full border-2"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2 text-green-500" />
                  Link Copied!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5 mr-2" />
                  Copy Link
                </>
              )}
            </Button>

            {showShareOption && (
              <Button
                onClick={shareLink}
                variant="ghost"
                className="w-full h-10 text-stone-500"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share via...
              </Button>
            )}
          </div>

          {/* URL Display */}
          <div className="mt-6 p-3 bg-gray-100 rounded-lg">
            <p className="text-xs text-stone-500 mb-1">Link to copy:</p>
            <p className="text-xs text-stone-600 break-all font-mono select-all">
              {window.location.href}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Normal browser - render children
  return children;
};

export default InstagramBrowserDetector;
